import { PublicRecord } from 'src/app/module/new-order/interface/new-order.interface';
import { ShortTermRental } from 'src/app/module/short-term-rental/interface/short-term-rental.interface';
import {
  UnitAdjustmentRecommendationSet,
  ValueAdjustmentRowConfig,
} from '../../adjustment-grid/interface/adjustment.interface';
import { FileType } from '../../file-viewer/interface/file-viewer.interface';
import { MultiPolygon } from '../../google-map/interface/google-map.interface';
import { OrderStatus } from '../../order-list/interface/order-list.interface';
import {
  ExtendedPropertyApiDetail,
  ExtendedPropertyDetail,
  PropertyAttribute,
  PropertyRole,
  PropertySummary,
} from '../../property/interface/property.interface';
import { CostApproach } from './cost-approach.interface';
import { ProjectInformation } from './project-information.interface';
import { SimilarityScoreAttributes } from './similarity-score.interface';
import { OrderMultiDataSource, ValueReconciliation } from './value-reconciliation.interface';

export interface OrderDetailApiResponse {
  order: OrderDetail;
}

export interface OrderDetail {
  additionalComments?: Array<{ key: string; value: string }>;
  additionalFiles?: AdditionalDoc[];
  appraisalForm: string;
  asyncDataStatus?: AsyncDataStatus;
  certification: Certification;
  city: string;
  clientInfo: ClientInfo;
  comments: string;
  companyKey: string;
  createdBy: string;
  createdOn: string;
  createdUserFullName?: string | null;
  effectiveDate?: Date | null;
  id: string;
  inspectionFetchStatus: NotificationStatus | null;
  inspectionOrderId: string;
  inspectionProvider: string;
  isRejected?: boolean;
  lastUpdatedAt: string;
  lastUpdatedBy: string;
  ListingId?: string | null;
  notes?: Array<Note>;
  offeringKey?: string;
  orderId: string;
  orderStatus: OrderStatus;
  ownedUserFullName?: string | null;
  PMXPropertyId?: string | null;
  projectInformation?: ProjectInformation;
  rejectionReasons?: string[];
  reportType: string;
  runCount?: number | null;
  state: string;
  streetAddress: string;
  userName: string;
  valueReconciliation?: ValueReconciliation;
  zip: string;
  orderAddress: string;
  supportsUnitAndStructure: boolean;
  latitude?: string | null;
  longitude?: string | null;
  draftInfo: DraftInfo;
}

export interface DuplicateOrderList extends OrderDetail {
  reportTypeDisplayValue?: string;
}
export interface AsyncDataStatus {
  listingHistory?: AsyncDataStatusType | null;
  deedHistory?: AsyncDataStatusType | null;
  imageWithLabels?: AsyncDataStatusType | null;
  neighborhood?: AsyncDataStatusType | null;
}

export type AsyncDataStatusKey = keyof AsyncDataStatus;

export enum AsyncDataStatusType {
  completed = 'COMPLETED',
  pending = 'PENDING',
  failed = 'FAILED',
}

export interface SalesComparison {
  adjustment?: UnitAdjustment;
  comments?: string | null;
  comparableList?: string[];
  compsSearched?: boolean;
  similarityScoreWeights?: SimilarityScoreAttributes | null;
  derivedPrice?: number;
  derivedPricePerSqft?: number;
  primarySearchFilter?: PrimarySearchFilter | null;
  secondarySearchFilter?: SecondarySearchFilter | null;
  selectedComparableList?: SelectedComparable[] | null;
  customValueAdjustmentLineItem?: ValueAdjustmentRowConfig[];
  marketDataComments?: string | null;
  compsLocationVerified?: boolean | null;
  compsLocationFactorVerified?: boolean | null;
}

export interface SelectedComparable {
  id: string;
  ListingId: string;
}

export interface PrimarySearchFilter {
  bathsRange?: ValueRangeWithTolerance<number | null>;
  halfBathsRange?: ValueRangeWithTolerance<number | null>;
  fullBathsRange?: ValueRangeWithTolerance<number | null>;
  bedsRange?: ValueRangeWithTolerance<number | null>;
  effectiveDate?: Date | null;
  includePublicRecordSales?: boolean;
  limitToSubjectZip?: boolean;
  listDateRange?: ValueRange<Date | null> | null;
  livingAreaRange?: ValueRangeWithTolerance<number | null>;
  lotSizeRange?: ValueRangeWithTolerance<number | null>;
  monthsBack?: number | null;
  offMarketStatusList?: OffMarkerType[] | null;
  polygonList?: MultiPolygon | null;
  priceRange?: ValueRange<number | null> | null;
  propertyTypeList?: PropertyType[] | null;
  radius?: number | null;
  saleType?: SaleType | null;
  schoolDistrict?: string | null;
  searchMode?: CompSearchType;
  soldDateRange?: ValueRange<Date | null> | null;
  statusList?: StatusType[] | null;
  storiesRange?: ValueRangeWithTolerance<number | null>;
  timeframeOption?: TimeFrameType;
  valuationType: ValuationType;
  yearBuiltRange?: ValueRangeWithTolerance<number | null>;
  filterSource?: ValuationType;
  propertyConditionRange?: ValueRange<string | null> | null;
  propertyQualityRange?: ValueRange<string | null> | null;
}

export interface PropmixAdjustmentSetting {
  livingAreaContributionPercentage: number;
  lotSizeContributionPercentage: number;
  bedroomContributionPercentage: number;
  bathroomContributionPercentage: number;
  garageParkingContributionPercentage: number;
  conditionStepContributionPercentage: number;
  qualityStepContributionPercentage: number;
  basementTotalAreaUnitAdjustmentPercentage: number;
  basementFinishedAreaUnitAdjustmentPercentage: number;
  numberOfStoriesContributionPercentage: number;
  ageContributionPercentage: number;
  totalRoomsContributionPercentage: number;
  adjustFor: string;
}

export interface MultiVariableRegressionAdjustmentModel {
  rsquared: number | null;
  standardError: number | null;
  fstatistic?: number | null;
  variableCount: number | null;
  comparableCount: number | null;
  variables: Array<MultiVariableRegression>;
}

export type MultiVariableRegression = {
  coefficient: number[] | null;
  pvalue: number | null;
  tvalue: number | null;
  standardError: number | null;
  variableId: string;
  variableName: PropertyAttribute | 'intercept';
  intervalLow: number;
  intervalHigh: number;
};

export type ValueAdjustmentDataSourceType =
  | 'propmixAdjustment'
  | 'pairedSalesAdjustment'
  | 'regressionAdjustment'
  | 'multiVariableRegressionAdjustment'
  | 'unitAdjustment';

export type PropertyDataSourceType =
  | 'listingRecord'
  | 'rentalRecord'
  | 'publicRecord'
  | 'inspectionRecord'
  | 'userRecord'
  | 'avmRecord';

export type DataSourceType = PropertyDataSourceType | ValueAdjustmentDataSourceType | keyof OrderMultiDataSource;

export type FilterSourceType = PropertyDataSourceType | 'effectiveSource';

export enum CompSearchType {
  radius = 'RADIUS',
  polygon = 'POLYGON',
}

export enum TimeFrameType {
  effectiveDate = 'effectiveDate',
  listingDate = 'listingDate',
}

export enum PropertyType {
  singleFamily = 'Single Family Residence',
  condominium = 'Condominium',
  duplex = 'Duplex',
  manufactured = 'Manufactured Home',
  mobile = 'Mobile Home',
  multiFamily = 'Multi Family',
  quadruplex = 'Quadruplex',
  triplex = 'Triplex',
  townhouse = 'Townhouse',
}

export enum SaleType {
  auction = 'auction',
  bankruptcy = 'bankruptcy',
  hud = 'hud',
  realEstateOwned = 'realEstateOwned',
  shortSale = 'shortSale',
  standard = 'standard',
}

export enum OffMarkerType {
  cancelled = 'Cancelled',
  delisted = 'Delisted',
  expired = 'Expired',
}

export enum StatusType {
  active = 'Active',
  sold = 'Sold',
  offMarket = 'OffMarket',
  pending = 'Pending',
}

export enum ValuationType {
  SALES = 'SALES',
  COST = 'COST',
  SHORT_TERM_RENTAL = 'SHORT_TERM_RENTAL',
  LONG_TERM_RENTAL = 'LONG_TERM_RENTAL',
  NEIGHBORHOOD = 'NEIGHBORHOOD',
}

export enum ApproachKey {
  salesComparison = 'salesComparison',
  costApproach = 'costApproach',
  shortTermRental = 'shortTermRental',
  longTermRental = 'longTermRental',
}

export type AnyApproach = SalesComparison | CostApproach | ShortTermRental | LongTermRental;

export type CommonApproachKeys = keyof AnyApproach;

export interface ValueRange<T> {
  from?: T;
  to?: T;
}

export interface ValueRangeWithTolerance<T> extends ValueRange<T> {
  tolerance?: number | null;
  source?: FilterSourceType | null;
}

export interface SecondarySearchFilter extends CompSearchSubFilter {
  pageNumber?: number;
  pageLimit?: number;
  similarityScoreType?: 'DEFAULT' | 'CUSTOM';
  fetchSelectedComparables?: boolean;
  sortOrder?: SortComparable;
  valuationType?: ValuationType;
}

export interface CompSearchSubFilter {
  includeHOA?: boolean | null;
  includeSolarPower?: boolean | null;
  includeGuestHouse?: boolean | null;
  includeFireplace?: boolean | null;
  includeGarage?: boolean | null;
  subDivisionName?: string;
  viewFactor?: ViewType | null;
  garageType?: GarageType | null;
  poolType?: PoolType | null;
  basementType?: BasementType | null;
  basementFinishedAreaRange?: ValueRange<number>;
  basementFinishedTotalAreaRange?: ValueRange<number>;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  propertyType?: PropertyType;
  status?: StatusType[] | null;
  bedsRange?: ValueRange<number>;
  bathsRange?: ValueRange<number>;
  livingAreaRange?: ValueRange<number>;
  listPriceRange?: ValueRange<number>;
  soldPriceRange?: ValueRange<number>;
  storiesRange?: ValueRange<number>;
  similarityScoreRange?: ValueRange<number>;
  propertyConditionRange?: ValueRange<string | null> | null;
  propertyQualityRange?: ValueRange<string | null> | null;
}

export type SecondarySearchFilterKeys = keyof SecondarySearchFilter;

export interface SortComparable {
  fieldName?: string;
  sortOrderType?: string;
}

export enum ViewType {
  waterFront = 'Water Front',
  woods = 'Woods',
  park = 'Park',
  pastoral = 'Pastoral',
  golfCourse = 'Gold Course',
  citySkyline = 'City Sky Line',
  mountain = 'Mountain',
  residential = 'Residential',
  cityStreet = 'City Street',
  industrial = 'Industrial',
  powerLines = 'Power Lines',
  limitedSight = 'Limited Sight',
}

export enum GarageType {
  attached = 'Attached',
  builtIn = 'Built-In',
  carPort = 'Car Port',
  detached = 'Detached',
  unknown = 'Unknown',
}

export enum PoolType {
  inGround = 'In Ground',
  aboveGround = 'Above Ground',
  unknown = 'Unknown',
}

export enum BasementType {
  crawlSpace = 'Crawl Space',
  fullBasement = 'Full Basement',
  partialBasement = 'Partial Basement',
  unknown = 'Unknown',
}

export type ComparableTableDataSource = ExtendedComparableProperty & {
  index: number;
  id: string;
  isSubject?: boolean;
  isSelectedComparable?: boolean;
  isSticky: boolean;
};

export interface ComparableSelectionEvent {
  id: string;
  isSelected: boolean;
}
export interface PropertyIdentifyEvent {
  comparableId: string;
  index: number;
}

export type ComparableProperty<T = any> = {
  [key in PropertyAttribute]?: T;
};

export type ExtendedComparableProperty = ComparableProperty & {
  index?: number;
  id?: string;
  valuationType?: ValuationType;
  isIdentified?: boolean;
  summary?: PropertySummary;
};

export interface GetComparablePropertyApiResponse {
  comparableList: ExtendedComparableProperty[];
  pageLimit: number;
  pageNumber: number;
  totalCount: number;
}

export interface GeocodeSearchParams {
  Street: string;
  City: string;
  State: string;
  Zip: string;
}

interface Geocode {
  Latitude?: string;
  Longitude?: string;
}

interface StandardizedAddressComponents {
  City: string;
  Country: string;
  State: string;
  StreetName: string;
  StreetPrefix?: string;
  StreetSuffix?: string;
  StreetNumber: string;
  StreetNumberPrefix?: string;
  UnitNumber?: string;
  Zip: string;
}

export interface GeocodeApiResponse {
  responseData: {
    Status: any;
    Data: {
      Geocode: Geocode;
      StandardizedAddress: string;
      StandardizedAddressComponents: StandardizedAddressComponents;
    };
  };
}

export interface AddComparableParams {
  id: string;
  scenarioId: string;
  listingId?: string;
  userRecord: Partial<Record<PropertyAttribute, any>>;
  valuationType: ValuationType;
  pmxPropertyId?: string;
}

export interface AddNewCustomComparableParams {
  id: string;
  scenarioId: string;
  userRecord: Partial<Record<PropertyAttribute, any>>;
  valuationType: ValuationType;
}

export interface AddNewCustomSubjectParams {
  userRecord: Partial<Record<PropertyAttribute, any>>;
}

export interface GetSelectedComparablePropertyApiResponse {
  totalCount: number;
  selectedComparables: ExtendedPropertyApiDetail[];
}

export interface ClientInfo {
  clientName?: string | null;
  clientFileId?: string | null;
  clientAddress?: string | null;
  intendedUseOfValuation?: string | null;
  ownerName?: string | null;
  borrowerName?: string | null;
  assignmentObjective?: string | null;
  intendedUseType?: string | null;
}

export interface DraftInfo {
  latitude?: string | null;
  longitude?: string | null;
}

export interface InspectionInfo {
  inspectionProvider?: string | null;
  inspectionOrderId?: string | null;
  inspectionType?: string | null;
  inspectionDate?: string | null;
  inspectorName?: string | null;
  inspectorPhone?: string | null;
  inspectorEmail?: string | null;
  inspectionRecordInfo?: Omit<InspectionInfo, 'inspectionRecordInfo'>;
}

export interface AdditionalDoc {
  url: string;
  fileReferenceId?: string;
  label: string; // File name by default
  fileType: FileType;
  isSelected: boolean;
  createdDate: Date;
}

export interface Certification {
  certificationList: string[];
  customCertificationList?: string[] | null;
  disclosureContent?: string;
  userName?: string;
  userPhoneNumber?: string;
  licenseNumber?: string;
  companyName?: string;
  companyAddress?: string;
  signDate?: Date | null;
  signImage?: string | null;
}

export interface OrderRoutePathConfig {
  route: string;
  displayValue: string;
  jsonPath: string;
  children: Array<OrderRoutePathConfig>;
  companySettingJsonPath?: string | string[] | null;
  validationGroup: string[];
  validationLabel: string;
  validationRoute: string;
  condition?: { route: string; value: boolean };
}

export interface AllStateCountyFips {
  counties: CountyFips;
}

export interface CountyFips {
  [key: string]: string;
}

export interface MLSBoard {
  FeedId: number;
  MLSAbbreviation: string;
  MLSBoard: string;
  MLSDisplayAbbreviation: string;
  MLSName: string;
  ParentMLSAbbreviation: string;
  ParentMLSName: string;
}

export interface ValidateComparableExistsAPIRes {
  listingId: string;
  propertyId: string;
  propertyRole: PropertyRole;
}

export interface ValidateComparableExistsRequest {
  id: string;
  scenarioId: string;
  listingId: string | null;
  pmxPropertyId: string | null;
  addressInfo: {
    streetAddress: string | null;
    unitNumber: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
  };
}

export interface AddCompValidationStatus {
  isDuplicate: boolean | null;
  isSelected: boolean | null;
  isRemovableComparable: boolean | null;
  notification: string;
  propertyInfo: ValidateComparableExistsAPIRes | null;
}

export interface AddCustomCompRes {
  listingId: string;
  propertyId: string;
}

export interface RedirectUrlApiRequest {
  id?: string;
  reportType?: string;
  action: string;
}

export interface RedirectUrlApiResponse {
  redirectURL: string;
  isOrderExistInMCA: boolean;
}

export interface PropertyMap {
  [key: string]: ExtendedPropertyDetail;
}

export interface SpecificComparable {
  Address?: string;
  ArchitecturalStyle?: string;
  BathroomsDecimal?: string;
  BathroomsFullCalculated?: string;
  BathroomsHalfCalculated?: string;
  BathroomsTotalInteger?: string;
  BedroomsTotal?: string;
  City?: string;
  CloseDate?: string;
  ClosePrice?: string;
  ConstructionQuality?: string;
  Cooling?: string;
  DataSource?: string;
  Distance?: string;
  FIPS?: string;
  FireplaceYN?: string;
  Flooring?: string;
  Heating?: string;
  ImageURLs?: string;
  Latitude?: string;
  ListingId?: string;
  LivingArea?: string;
  Longitude?: string;
  LotSizeSquareFeet?: string;
  MlsBoard?: string;
  MLSListingNumber?: string;
  OwnerName?: string;
  ParcelNumber?: string;
  ParkingSpaces?: string;
  PMXPropertyId?: string;
  PostalCode?: string;
  PropertySubType?: string;
  PropertyType?: string;
  PublicRecord?: PublicRecord;
  RoofCoverType?: string;
  RoomsTotal?: string;
  Source?: string;
  StateOrProvince?: string;
  Stories?: string;
  StreetAddress?: string;
  UnitNumber?: string;
  YearBuilt?: string;
}

export type PreviewSnapShotKeys = 'order' | 'subject' | 'neighborhoodInsight' | 'scenario' | ApproachKey;

export type PreviewSnapshot = {
  [key in PreviewSnapShotKeys]?: string;
};

export type NotificationStatus = 'SUCCESS' | 'INVALID' | 'FAILED' | 'PENDING';

export interface LongTermRental {
  comments?: string | null;
  compsSearched?: boolean;
  primarySearchFilter?: PrimarySearchFilter | null;
  secondarySearchFilter?: SecondarySearchFilter | null;
  selectedComparableList?: SelectedComparable[] | null;
  similarityScoreWeights?: SimilarityScoreAttributes | null;
  adjustment?: UnitAdjustment;
  customValueAdjustmentLineItem?: ValueAdjustmentRowConfig[];

  derivedMonthlyRent?: number | null;

  monthlyRent?: number | null;
  effectiveGrossIncome?: number | null;
  otherRevenue?: number | null;
  otherRevenueDescription?: string | null;
  vacancyRate?: number | null;
  potentialGrossIncome?: number | null;

  annualTax?: number | null;
  annualHoaFee?: number | null;
  insurance?: number | null;

  derivedManagementCost?: number | null;
  managementCostType?: 'PERCENT' | 'DETAILED' | 'VALUE';
  managementCostPercentage?: number | null;
  managementCostValue?: number | null;
  managementCostDetail?: Record<string, number>;

  annualExpense?: number | null;

  netOperatingIncome?: number | null;

  derivedValue?: number | null;
  incomeValueModel?: 'GRM' | 'DISCOUNTED_CASH_FLOW' | 'CAP_RATE';
  grmModel?: {
    postalCodeGRM?: number | null;
    rentComparableGRM?: number | null;
    grmComparableGRM?: number | null;
    customGRM?: number | null;
    selectedGRM?: 'PROPMIX_POSTAL_CODE' | 'RENT_COMPARABLE' | 'GRM_COMPARABLE' | 'CUSTOM';
  };
  propmixAdjustmentSettings?: PropmixAdjustmentSetting;
  valueComments?: string | null;
  marketDataComments?: string | null;
  marketRentComments?: string | null;
  compsLocationVerified?: boolean | null;
  compsLocationFactorVerified?: boolean | null;
}

export type ApproachTypeConfig = {
  [key in ApproachKey]: { valuationType: ValuationType; page: string; displayValue: string };
};

export interface UnitAdjustment extends UnitAdjustmentRecommendationSet {
  settings?: {
    propmixAdjustment?: PropmixAdjustmentSetting | null;
    regressionAdjustment?: RegressionAdjustmentSetting | null;
    multiVariableRegressionAdjustment?: MultiVariableRegressionAdjustmentSetting | null;
    pairedSalesAdjustment?: null;
    unitAdjustment?: null;
  };
}

export interface RegressionAdjustmentSetting {
  // variables?: Array<Partial<PropertyAttribute>>;
  autoSelectComps?: boolean;
  compFilter?: RegressionAdjustmentFilter | null;
}

export interface RegressionAdjustmentFilter {
  bedsRange?: ValueRange<number>;
  bathsRange?: ValueRange<number>;
  livingAreaRange?: ValueRange<number>;
  storiesRange?: ValueRange<number>;
  lotSizeRange?: ValueRange<number>;
  fetchSelectedComparables?: boolean;
  sortOrder?: SortComparable;
  pageNumber?: number;
  pageLimit?: number;
}

export interface MultiVariableRegressionAdjustmentSetting {
  variables?: Array<Partial<PropertyAttribute>>;
  autoSelectComps?: boolean;
  compFilter?: MultiVariableRegressionAdjustmentFilter | null;
}

export interface MultiVariableRegressionAdjustmentFilter {
  bedsRange?: ValueRange<number>;
  bathsRange?: ValueRange<number>;
  livingAreaRange?: ValueRange<number>;
  storiesRange?: ValueRange<number>;
  lotSizeRange?: ValueRange<number>;
  fetchSelectedComparables?: boolean;
  pageNumber?: number;
  pageLimit?: number;
}

export interface Note {
  title: string | null;
  content: string | null;
  createdAt: Date | null;
  updatedAt: Date | null;
  updatedBy: string | null;
  createdBy: string | null;
  editMode?: boolean | null;
}

export type GenerativeCommentType =
  | 'NEIGHBORHOOD'
  | 'SUBJECT'
  | 'RECONCILIATION'
  | 'COMPARABLE'
  | 'SALES_COMPARISON'
  | 'HOA'
  | 'PRIOR_SALES'
  | 'VALUE_CONDITION_STATEMENT';

export interface GenerativeCommentConfig {
  commentType: GenerativeCommentType;
  propertyId?: string;
  scenarioId?: string;
}

export interface OrderLockAPIResponse {
  orderId: string;
  heartbeatIntervalSec: number;
  lockToken: string;
  acquiredAt?: string;
  heartbeatAt: string;
  expiresAt: string;
}

export interface ReportSubmitResponse {
  redirectUrl?: string | null;
  source?: string | null;
}
